import { useState } from "react";

import { Box, Button, CircularProgress, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { ReactComponent as DotsThreeOutlineGrayIcon } from '../../../../assets/icons/DotsThreeOutlineGrayIcon.svg';
import { Trash } from "@phosphor-icons/react";

import type { VectorEmbeddingModel } from "../../../../types/vector";
import type { MutationStatus } from "@tanstack/react-query";

interface VectorItemProps {
    deleteStatus: MutationStatus
    embeddingModelName: VectorEmbeddingModel
    id: string
    name: string
    selectedNamespace: string | undefined
    onCloseSideBarMobile: VoidFunction
    onOpenVectorModal: ({
        namespaceId, open
    }: {
        open: boolean
        namespaceId: string
    }) => void
    onAlertOpen: (open: boolean) => void
    onSelectedNamespaceChange: ({
        embeddingModelName, id, name
    }: {
        embeddingModelName: VectorEmbeddingModel
        id: string
        name: string
    }) => void
}

function VectorItem({
    deleteStatus,
    embeddingModelName,
    id,
    name,
    selectedNamespace,
    onAlertOpen,
    onCloseSideBarMobile,
    onOpenVectorModal,
    onSelectedNamespaceChange
}: VectorItemProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleOptionsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }

    const handleOptionsClose = () => {
        setAnchorEl(null);
    }

    const namespaceLabel = {
        idle: (namespaceName: string) => namespaceName,
        pending: () => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CircularProgress size={14} />
            Deleting
        </span>
        ),
        error: () => 'Error, try again!',
        success: () => 'Delete successfully'
    }


    return (
        <Box
            className="menu menu--on-hover"
            sx={{
                paddingLeft: '20px',
                paddingBlock: '0.25rem',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': {
                    backgroundColor: '#E7F0FF',
                    borderRadius: '4px',
                },
            }}
        >
            <Tooltip title={name}>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#475467',
                        fontSize: '16px',
                        margin: '0',
                        width: '13rem',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    }}
                    onClick={() => {
                        onCloseSideBarMobile();
                        onOpenVectorModal({
                            open: true,
                            namespaceId: id
                        });
                    }}
                >
                    {selectedNamespace === name ? namespaceLabel[deleteStatus](name) : name}
                </Typography>
            </Tooltip>

            <IconButton
                size="medium"
                onClick={handleOptionsOpen}
                sx={{ padding: '0.25rem' }}
            >
                <DotsThreeOutlineGrayIcon />
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleOptionsClose}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: '8px',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="text"
                        sx={{
                            width: '100%',
                            padding: '0.5rem',

                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',

                            color: '#b91c1c',

                            '&:hover': {
                            backgroundColor: '#fecdd3',
                            }
                        }}
                        onClick={() => {
                            onAlertOpen(true)
                            onSelectedNamespaceChange({ embeddingModelName, id, name })
                        }}
                    >
                        <Trash size={20} />

                        Delete
                    </Button>
                </Box>
            </Popover>
        </Box>
    )
}

export { VectorItem }